/*
 * <product-addon> — an opt-in extra that goes into the cart with the product.
 *
 * Shopify's product form posts exactly one variant, so a second item cannot
 * ride along on the same request. The add-on therefore goes in as its own Cart
 * AJAX call, ordered immediately before the main one.
 *
 * It hooks the cart request rather than the form's submit event, because the
 * shoe PDPs are split across two systems and only one of them submits a form:
 * the theme's own product form does, GemPages' buy button does not — it calls
 * the cart API directly. Both, however, reach /cart/add through fetch, so
 * intercepting that covers every buy button on the store, including ones
 * nobody has built yet.
 *
 * Ordering matters twice over. The add-on goes in BEFORE the main item, so the
 * cart drawer — which renders off the main add's response — already contains
 * it. And if the add-on request fails, the main one still proceeds: a shopper
 * who asked for shoes gets shoes. Failing a purchase because an upsell failed
 * would be the wrong trade.
 */
/* Wrapped so the file is safe to include twice: both the socks row and the
   membership gift load it, and only the first copy should install the hook. */
;(() => {
  if (window.ProductAddon) return

  const CART_ADD = /\/cart\/add(\.js)?(\?|$)/

  /* Marks our own request so the interceptor does not re-enter on it. */
  const INTERNAL = 'x-addon-internal'

  let installed = false

  /* Every element that can ride along on a cart add: the socks row and the
   membership gift both register with this attribute and share one hook. */
  function addonElements() {
    return Array.from(document.querySelectorAll('[data-cart-addon]')).filter((el) => typeof el.wantsAdd === 'function')
  }

  /*
   * The variant ids a /cart/add request carries, so an add-on can tell the page's
   * own buy button from a cart-drawer upsell. GemPages posts FormData, the theme
   * posts JSON; anything unreadable comes back empty rather than guessed.
   */
  function requestVariantIds(body) {
    try {
      if (!body) return []
      if (typeof FormData !== 'undefined' && body instanceof FormData) return body.getAll('id').map(Number)
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
        return body.getAll('id').map(Number)
      if (typeof body === 'string') {
        if (body.trim().charAt(0) === '{') {
          const data = JSON.parse(body)
          if (Array.isArray(data.items)) return data.items.map((item) => Number(item.id))
          return data.id ? [Number(data.id)] : []
        }
        return new URLSearchParams(body).getAll('id').map(Number)
      }
    } catch (e) {}
    return []
  }

  /*
   * Resolve one anchor entry to the element the row should be inserted before.
   *
   * An entry may be "selector" or "selector|ancestor". The second form finds the
   * element, then climbs to its nearest matching ancestor, which is how you get
   * above a whole layout row rather than into one of its columns: the GemPages
   * shipping-estimate line is one column of a grid whose other column holds the
   * "Few spots left" pill, so inserting beside the estimate leaves the card
   * squeezed between them at half width.
   */
  function resolveAnchor(entry) {
    const parts = entry
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
    if (!parts.length) return null

    let node
    try {
      node = document.querySelector(parts[0])
    } catch (e) {
      return null
    }
    if (!node) return null

    /* Each further step climbs one nesting level, so the same ancestor can be
     named twice to get out of a row that is itself inside a row — which is how
     the GemPages estimate line is built. */
    for (const ancestor of parts.slice(1)) {
      let next
      try {
        next = node.parentElement && node.parentElement.closest(ancestor)
      } catch (e) {
        break
      }
      if (!next) break
      node = next
    }

    return node
  }

  /*
   * Between the add-on request and the main one the cart briefly holds the
   * add-on alone. Cart checks that judge the cart's contents (the membership
   * cart guard) wait on ProductAddon.pending instead of acting on that moment,
   * and compare ProductAddon.adds to tell whether an add ran while they looked.
   */
  function trackPending(chain) {
    window.ProductAddon.adds = (window.ProductAddon.adds || 0) + 1
    const settled = chain.then(
      () => {},
      () => {},
    )
    const pending = Promise.all([window.ProductAddon.pending, settled]).then(() => {
      if (window.ProductAddon.pending === pending) window.ProductAddon.pending = null
    })
    window.ProductAddon.pending = pending
  }

  function cartAddUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
    return `${root}cart/add.js`
  }

  function installInterceptor() {
    if (installed) return
    const originalFetch = window.fetch
    if (typeof originalFetch !== 'function') return
    installed = true

    window.fetch = function (input, init) {
      let url = ''
      try {
        url = typeof input === 'string' ? input : (input && input.url) || ''
      } catch (e) {
        url = ''
      }

      const isOurs = !!(init && init.headers && init.headers[INTERNAL])
      const candidates = CART_ADD.test(url) && !isOurs ? addonElements() : []
      if (!candidates.length) return originalFetch.apply(this, arguments)

      const args = arguments
      const self = this
      const variantIds = requestVariantIds(init && init.body)

      /* An add-on may need a decision before the add goes through (the
       membership pop-up asks for one); the main request waits for it. */
      return Promise.all(candidates.map((el) => Promise.resolve(el.prepare(variantIds)).catch(() => {}))).then(() => {
        const addons = candidates.filter((el) => el.wantsAdd(variantIds))
        if (!addons.length) return originalFetch.apply(self, args)

        const items = addons.map((el) => el.cartItem())

        /* Settle before the request rather than after: the item is on its way
         in, and leaving it pending would add it again on the next click. */
        addons.forEach((el) => el.onAdded())

        const chain = originalFetch
          .call(self, cartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              [INTERNAL]: '1',
            },
            body: JSON.stringify({items}),
          })
          .catch(() => {})
          .then(() => originalFetch.apply(self, args))
        trackPending(chain)
        return chain
      })
    }
  }

  class ProductAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return
      this.initialised = true

      this.toggle = this.querySelector('[data-addon-toggle]')
      if (!this.toggle) return

      if (this.dataset.anchor) this.moveToAnchor()

      this.toggle.addEventListener('click', () => this.setChecked(!this.checked))
      this.toggle.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault()
          this.setChecked(!this.checked)
        }
      })

      installInterceptor()
    }

    /*
     * Rendered by the layout and moved into position here, so that a page the
     * theme does not own — a GemPages product page — can carry the row without
     * anything being added to the GemPages document itself. Republishing from
     * the GemPages editor therefore cannot remove it.
     *
     * The buy button is a custom element that GemPages upgrades after its own
     * scripts run, so the anchor is not necessarily in the DOM at this point.
     * Watch for it, and give up rather than observe forever on a page that will
     * never have one.
     */
    moveToAnchor() {
      /* A comma-separated list, tried in order rather than handed to
       querySelector as one selector: the order expresses priority — the
       GemPages buy button first, the theme's own buy block second — whereas
       querySelector would return whichever came first in the document, which
       on some pages is a sticky bar. */
      const selectors = this.dataset.anchor
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

      const place = () => {
        for (const entry of selectors) {
          const anchor = resolveAnchor(entry)
          if (!anchor || anchor.contains(this) || !anchor.parentNode) continue

          anchor.parentNode.insertBefore(this, anchor)
          this.hidden = false
          return true
        }
        return false
      }

      if (place()) return

      const observer = new MutationObserver(() => {
        if (place()) observer.disconnect()
      })
      observer.observe(document.body, {childList: true, subtree: true})
      setTimeout(() => observer.disconnect(), 15000)
    }

    get checked() {
      return this.toggle.getAttribute('aria-checked') === 'true'
    }

    setChecked(on) {
      this.toggle.setAttribute('aria-checked', on ? 'true' : 'false')
      this.toggle.classList.toggle('is-checked', on)
    }

    /* Cart-add hooks; subclasses override them (see membership-offer.js). */
    prepare() {}

    wantsAdd() {
      return this.checked
    }

    cartItem() {
      return {
        id: Number(this.dataset.variantId),
        quantity: Number(this.dataset.quantity) || 1,
      }
    }

    onAdded() {
      this.setChecked(false)
    }
  }

  window.ProductAddon = ProductAddon

  if (!customElements.get('product-addon')) {
    customElements.define('product-addon', ProductAddon)
  }
})()
